import { Box, Button, Dialog, IconButton, Slide, Typography } from '@mui/material';
import React, { forwardRef, useEffect, useState } from 'react';
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useForm } from 'react-hook-form';
import { MAX_LENGTH_VALIDATION } from 'utils/errorMessages';
import BaseInput from '../baseForm/BaseInput';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
});

export default function MarketplaceSubItemModal({
  open,
  selectedItem,
  currentSubItems = {},
  note = "",
  handleClose
}) {
  const [selectedSubItems, setSelectedSubItems] = useState([]);
  const methods = useForm({
    shouldUnregister: false,
    mode: "all",
  });
  
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = methods;

  useEffect(() => {
    setSelectedSubItems(currentSubItems);
    if (note?.length) {
      setValue("note", note);
    }
  }, [currentSubItems, note]);

  const handleItemClick = (item) => {
    setSelectedSubItems((prev) => {
      if (prev[item.id]) return prev;
      return { ...prev, [item.id]: { ...item, quantity: 1} };
    });
  };

  const handleQuantityChange = (itemId, delta) => {
    setSelectedSubItems((prev) => {
      const current = prev[itemId];
      if (!current) return prev;
      const newQty = current.quantity + delta;
      if (newQty <= 0) {
        // eslint-disable-next-line no-unused-vars
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: { ...current, quantity: newQty } };
    });
  };

  return (
    <Dialog open={open} TransitionComponent={Transition}>
      <Box className="customScrollbar"
        sx={{
          backgroundColor: "#f4f9f9",
          border: "2px solid #737373",
          borderRadius: ".5rem",
          overflowY: "auto",
          width: "425px"
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flex: "1 0 100%",
            marginBottom: "1rem"
          }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ padding: "1rem 0 0 1.5rem" }}>
            {selectedItem?.name}
          </Typography>
          <IconButton
            sx={{
              marginRight: "1rem",
              marginLeft: "auto",
              marginTop: "1rem",
              alignSelf: "flex-start",
              color: "var(--redColor)",
              height: "2rem",
              width: "2rem",
            }}
            onClick={() =>
              handleClose(
                selectedItem.id,
                selectedSubItems,
                getValues("note")
              )
            }
            aria-label="Close">
            <CloseIcon sx={{ fontSize: "2rem" }} />
          </IconButton>
        </Box>

        <Box
          px={3}
        >
          {selectedItem?.subItems?.map((item) => {
            const isSelected = !!selectedSubItems[item.id];

            return (
              <Box 
                key={item.id}
                onClick={() => handleItemClick(item)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: ".5rem 1rem",
                  mb: ".5rem",
                  borderRadius: ".5rem",
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#cee2e3" : "#fff",
                  border: "1px solid rgba(128,128,128,.3)",
                  "&:hover": {
                    backgroundColor: isSelected ? "#bdd6d7" : "#f5f5f5",
                  },
                }}
              >
                <Typography>
                  {item.name}
                </Typography>
                <Typography fontSize=".875rem" ml="auto" mr=".25rem">
                  ${item?.price?.toFixed(2) ?? "0.00"}
                </Typography>
                {isSelected && (
                  <Box
                    display="flex"
                    alignItems="center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, -1)}
                    >
                      <RemoveCircleOutlineIcon sx={{fontSize: "1.5rem"}} />
                    </IconButton>
                    <Typography
                      sx={{ 
                        minWidth: "1.5rem", 
                        textAlign: "center", 
                        fontWeight: "bold",
                        fontSize: ".875rem" 
                      }}
                    >
                      {selectedSubItems[item.id].quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, 1)}
                      disabled={selectedSubItems[item.id].quantity >= 99}
                    >
                      <AddCircleOutlineIcon sx={{fontSize: "1.5rem"}} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            padding: ".5rem 1.5rem 1.5rem 1.5rem",
            display: "flex",
            flexWrap: "wrap",
            flex: "1 0 100%",
          }}>
          <Typography sx={{ width: "100%" }} variant="h6">
            Note
          </Typography>
          <BaseInput
            control={control}
            errors={errors}
            id="note"
            name="note"
            multiline
            variant="outlined"
            rows="2"
            fullWidth
            margin="normal"
            sx={{ backgroundColor: "white !important" }}
            validationProps={{
              maxLength: MAX_LENGTH_VALIDATION("Note", 200)
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 1.5rem",
          }}
        >
          <Button
            sx={{
              width: "fit-content",
              alignSelf: "flex-end",
              fontWeight: "bold",
              marginBottom: "1rem",
              color: "#FFF",
            }}
            variant="contained"
            onClick={() => {
              if (errors?.specialNote) {
                return;
              }
              handleClose(
                selectedItem.id,
                selectedSubItems,
                getValues("note")
              );
            }}
            endIcon={
              <AddCircleIcon
                sx={{ marginBottom: ".25rem", transform: "scale(1.25)" }}
              />
            }>
            Add
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}